import { useState, type CSSProperties } from 'react';
import { IconBack } from './components/icons';

// Evaluate flow — the SPECIALTY-FIRST step. Shown AFTER the user provides their CV (paste/upload) but BEFORE the
// parse, in EVALUATE mode only. Stating the specialty up front makes the wait legible ("reading against what
// {specialty} programs look for") and gives the read a clear lens. STATE-only: the chosen id is held by the parent
// and threaded into the existing commit (POST /api/entries) — no API call here, no parse trigger until "start".
//
// HONEST FRAMING: hedged ("tend to look for"), no evidence claim, no guarantee. The optional PROGRAM layer stays
// POST-read (the program list is per-CV, which doesn't exist until commit) — out of scope here.

interface Specialty { id: string; name: string }

export function EvalSetup({ specialties, onStart, onBack }: {
  specialties: Specialty[];
  onStart: (specialtyId: string, stream: string) => void;
  onBack: () => void;
}) {
  const [specialtyId, setSpecialtyId] = useState('');
  const [stream, setStream] = useState(''); // 'img' | 'cmg' — gates the IMG presence-check panel on the read
  const chosen = specialties.find((s) => s.id === specialtyId);
  const ready = !!specialtyId && !!stream;

  return (
    <main style={S.main} data-testid="eval-setup">
      <button className="btn btn-ghost" style={S.back} onClick={onBack} data-testid="eval-setup-back"><IconBack aria-hidden="true" /> Use a different CV</button>
      <h1>Which specialty should we read this against?</h1>
      <p style={S.help}>
        We’ll read your CV against what programs in this area <em>tend to</em> look for — so the wait shows what’s
        happening, and the read has a clear lens. You can change this later.
      </p>

      <label style={S.label}>Specialty
        <select style={S.sel} value={specialtyId} onChange={(e) => setSpecialtyId(e.target.value)} data-testid="eval-setup-specialty">
          <option value="">— choose a specialty —</option>
          {specialties.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </label>

      <label style={S.label}>Which stream are you applying through?
        <select style={S.sel} value={stream} onChange={(e) => setStream(e.target.value)} data-testid="eval-setup-stream">
          <option value="">— choose a stream —</option>
          <option value="cmg">CMG — Canadian medical graduate</option>
          <option value="img">IMG — international medical graduate</option>
        </select>
      </label>

      <div style={S.bar}>
        <button className="btn btn-primary" disabled={!ready} onClick={() => ready && onStart(specialtyId, stream)} data-testid="eval-setup-start">
          {chosen ? <>Review my CV against {chosen.name} <IconBack aria-hidden="true" style={{ transform: 'rotate(180deg)' }} /></> : <>Review my CV <IconBack aria-hidden="true" style={{ transform: 'rotate(180deg)' }} /></>}
        </button>
        {!ready && <span style={S.hint} data-testid="eval-setup-need">Choose a specialty and your stream first.</span>}
      </div>
    </main>
  );
}

const S: Record<string, CSSProperties> = {
  main: { maxWidth: 620, margin: '40px auto', padding: '0 var(--space-4)', fontFamily: 'var(--sans)', lineHeight: 'var(--lh-body)' },
  back: { marginLeft: 0, paddingLeft: 0, fontSize: 'var(--fs-small)', marginBottom: 'var(--space-2)' },
  help: { color: 'var(--muted)', margin: 'var(--space-1) 0 var(--space-4)' },
  label: { display: 'block', margin: 'var(--space-2) 0', fontSize: 'var(--fs-body)' },
  sel: { display: 'block', padding: 'var(--space-2)', fontSize: 'var(--fs-body)', marginTop: 'var(--space-1)', minWidth: 260 },
  bar: { display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flexWrap: 'wrap', marginTop: 'var(--space-5)' },
  hint: { fontSize: 'var(--fs-small)', color: 'var(--muted)' },
};
